import React, { useEffect, useState } from 'react';
import axios from '../api';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Grid,
  Typography,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  TextField,
  Autocomplete,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import QuotationForm from '../components/QuotationForm';
import { useToast } from '../components/ToastProvider';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx, tableHeaderSx } from '../styles/tokens';
import { formatDate } from '../utils/dateUtils';
import { generateQuotationPdf } from '../utils/generatePdf';
import { useUser } from '../components/UserContext';

const VALID_CONVERSION = new Set(['all', 'converted', 'not_converted']);

const Quotations = () => {
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isWarehouse = user?.role === 'warehouse';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotations, setQuotations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const conversionFilter = VALID_CONVERSION.has(searchParams.get('status'))
    ? searchParams.get('status')
    : 'all';
  const showToast = useToast();
  const navigate = useNavigate();

  const setConversionFilter = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all') next.delete('status');
      else next.set('status', value);
      return next;
    }, { replace: true });
  };

  const listReturnPath = `${location.pathname}${location.search}`;

  const openQuotationDetails = (id) => {
    navigate(`/quotations/${id}`, { state: { fromQuotations: listReturnPath } });
  };

  const filteredQuotations =
    conversionFilter === 'all'
      ? quotations
      : quotations.filter((q) =>
          conversionFilter === 'converted' ? q.is_converted : !q.is_converted
        );

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/quotations');
      setQuotations(res.data);
    } catch (error) {
      showToast('Error fetching quotations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  // Open edit modal when navigating from detail page with ?edit=id
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && quotations.length > 0) {
      const id = parseInt(editId, 10);
      if (Number.isNaN(id)) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
      openEdit(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('edit'), quotations.length]);

  const openDetails = async (groupId) => {
    try {
      const res = await axios.get(`/api/quotations/${groupId}`);
      setDetails(res.data);
      setModalOpen(true);
    } catch (error) {
      showToast('Error loading quotation details', 'error');
    }
  };

  const openQuotationPdf = async (groupId) => {
    try {
      await generateQuotationPdf(groupId);
    } catch (error) {
      showToast(error.message || 'Failed to generate PDF', 'error');
    }
  };

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`/api/quotations/${id}`);
      setEditData(res.data);
      setEditModalOpen(true);
    } catch (error) {
      showToast('Error loading quotation for edit', 'error');
    }
  };

  const closeEdit = () => {
    setEditModalOpen(false);
    setEditData(null);
  };

  const handleEditSubmit = async (payload) => {
    try {
      await axios.patch(`/api/quotations/${editData.id}`, payload);
      closeEdit();
      fetchQuotations();
      showToast('Quotation updated successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to update quotation', 'error');
    }
  };

  const [convertingId, setConvertingId] = useState(null);
  const handleConvertToBooking = async (q) => {
    if (q.is_converted) return;
    if (!q.event_date || !q.return_date) {
      showToast('Please set Event Date and Return Date (edit quotation) before converting to booking.', 'warning');
      return;
    }
    setConvertingId(q.id);
    try {
      await axios.post(
        `/api/quotations/${q.id}/convert-to-booking`,
        { event_date: q.event_date, return_date: q.return_date }
      );
      fetchQuotations();
      showToast('Quotation converted to booking successfully.', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to convert to booking.', 'error');
    } finally {
      setConvertingId(null);
    }
  };

  const statusChipStyles = {
    converted: { bg: 'rgba(34, 197, 94, 0.12)', dot: '#16a34a', text: '#15803d' },
    notConverted: { bg: 'rgba(107, 114, 128, 0.15)', dot: '#6b7280', text: '#4b5563' },
  };

  return (
    <Box sx={pageContainerSx}>
      <PageHeader
        title="Quotations"
        subtitle="Create and manage quotations"
        actionLabel={!isWarehouse ? 'New Quotation' : undefined}
        actionIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onAction={!isWarehouse ? () => setShowQuotationForm(true) : undefined}
      />

      {showQuotationForm && (
        <QuotationForm
          closeModal={() => {
            setShowQuotationForm(false);
            fetchQuotations();
          }}
        />
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ width: { xs: '100%', sm: 128 }, height: 32 }}>
          <InputLabel id="conversion-filter-label">Status</InputLabel>
          <Select
            labelId="conversion-filter-label"
            value={conversionFilter}
            label="Status"
            onChange={(e) => setConversionFilter(e.target.value)}
            sx={{ height: 32, fontSize: '0.75rem', borderRadius: 1, '& .MuiSelect-select': { py: 0.75 }, '& fieldset': { borderColor: 'divider' } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
            <MenuItem value="not_converted">Not converted</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
          width: '100%',
          minWidth: 0,
        }}
      >
        {loading ? (
          <PageLoader variant="section" message="Loading quotations..." />
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filteredQuotations.length === 0 ? (
                  <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>No quotations found</Typography>
                ) : (
                  filteredQuotations.map((q) => {
                    const sc = q.is_converted ? statusChipStyles.converted : statusChipStyles.notConverted;
                    return (
                      <Paper key={q.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Button variant="text" size="small" onClick={() => openDetails(q.id)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>#{q.id}</Button>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>{q.customer}</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{q.company_name || '—'}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>Quotation: {formatDate(q.quotation_date) || '—'} · Event: {formatDate(q.event_date) || '—'} · Return: {formatDate(q.return_date) || '—'}</Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sc.dot }} />
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{q.is_converted ? 'Converted' : 'Not converted'}</Typography>
                              <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', ml: 1 }}>₹{q.total_amount != null ? q.total_amount : 0}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <IconButton size="small" onClick={() => openQuotationDetails(q.id)} sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton>
                            {!q.is_converted && <IconButton size="small" onClick={() => openEdit(q.id)} sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><EditIcon sx={{ fontSize: 18 }} /></IconButton>}
                            <IconButton size="small" onClick={() => openQuotationPdf(q.id)} sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><PictureAsPdfIcon sx={{ fontSize: 18 }} /></IconButton>
                            <IconButton size="small" onClick={() => handleConvertToBooking(q)} disabled={q.is_converted || convertingId === q.id} sx={{ width: 32, height: 32, color: q.is_converted ? 'text.disabled' : 'success.main', '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' } }}>
                              {convertingId === q.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })
                )}
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={tableHeaderSx}>Quotation #</TableCell>
                      <TableCell sx={tableHeaderSx}>Customer</TableCell>
                      <TableCell sx={tableHeaderSx}>Company</TableCell>
                      <TableCell sx={tableHeaderSx}>Quotation Date</TableCell>
                      <TableCell sx={tableHeaderSx}>Event Date</TableCell>
                      <TableCell sx={tableHeaderSx}>Return Date</TableCell>
                      <TableCell sx={tableHeaderSx}>Status</TableCell>
                      <TableCell sx={tableHeaderSx}>Amount</TableCell>
                      <TableCell align="center" sx={{ ...tableHeaderSx, width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQuotations.length === 0 ? (
                      <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>No quotations found</TableCell></TableRow>
                    ) : (
                      filteredQuotations.map((q) => {
                        const sc = q.is_converted ? statusChipStyles.converted : statusChipStyles.notConverted;
                        return (
                          <TableRow key={q.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.15s' }}>
                            <TableCell sx={{ px: 2, py: 1.5 }}>
                              <Button variant="text" size="small" onClick={() => openDetails(q.id)} sx={{ textTransform: 'none', fontWeight: 400, fontSize: '13px', color: 'text.primary', p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>#{q.id}</Button>
                            </TableCell>
                            <TableCell sx={{ px: 2, py: 1.5 }}><Typography component="span" sx={{ fontSize: '13px', color: 'text.primary' }}>{q.customer}</Typography></TableCell>
                            <TableCell sx={{ px: 2, py: 1.5, fontSize: '13px', color: 'text.secondary' }}>{q.company_name || '—'}</TableCell>
                            <TableCell sx={{ px: 2, py: 1.5, fontSize: '13px' }}>{formatDate(q.quotation_date) || '—'}</TableCell>
                            <TableCell sx={{ px: 2, py: 1.5, fontSize: '13px' }}>{formatDate(q.event_date) || '—'}</TableCell>
                            <TableCell sx={{ px: 2, py: 1.5, fontSize: '13px' }}>{formatDate(q.return_date) || '—'}</TableCell>
                            <TableCell sx={{ px: 2, py: 1.5 }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.875rem', color: 'text.primary' }}>
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sc.dot }} />{q.is_converted ? 'Converted' : 'Not converted'}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ px: 2, py: 1.5 }}><Typography component="span" sx={{ fontSize: '13px', color: 'text.primary' }}>₹{q.total_amount != null ? q.total_amount : 0}</Typography></TableCell>
                            <TableCell align="center" sx={{ px: 2, py: 1.5 }}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="View details"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openQuotationDetails(q.id); }} sx={{ width: 28, height: 28, color: 'text.secondary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                {!q.is_converted && <Tooltip title="Edit quotation"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(q.id); }} sx={{ width: 28, height: 28, color: 'text.secondary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                                <Tooltip title="Export PDF"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openQuotationPdf(q.id); }} sx={{ width: 28, height: 28, color: 'text.secondary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}><PictureAsPdfIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                <Tooltip title={q.is_converted ? 'Already converted' : 'Convert to booking'}>
                                  <span>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleConvertToBooking(q); }} disabled={q.is_converted || convertingId === q.id} sx={{ width: 28, height: 28, color: q.is_converted ? 'text.disabled' : 'success.main', borderRadius: 1.25, '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover', color: 'success.dark' }, '&.Mui-disabled': { color: 'text.disabled' } }}>
                                      {convertingId === q.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid',
                borderColor: 'divider',
                px: 2,
                py: 1.25,
              }}
            >
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                Showing{' '}
                <Typography component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>{filteredQuotations.length}</Typography>{' '}
                quotation{filteredQuotations.length !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" disabled sx={{ width: 28, height: 28, borderRadius: 1.25, color: 'text.secondary', '&.Mui-disabled': { color: 'text.disabled' }, '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' } }}>
                  <ChevronLeftIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '11px', fontWeight: 500, '&:hover': { bgcolor: 'primary.dark' } }}>
                  1
                </IconButton>
                <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: 1.25, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                  <ChevronRightIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Paper>

        {/* Detail Dialog */}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: '1.125rem',
              py: 2.25,
              px: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>Quotation Details</Typography>
            </Box>
            <IconButton onClick={() => setModalOpen(false)} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
              <span style={{ fontSize: 24 }}>×</span>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
            {details ? (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0', height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#424242', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" /> Customer & Dates
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Customer</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{details.customer}</Typography>
                      </Box>
                      {details.company_name && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Company</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{details.company_name}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Quotation Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(details.quotation_date) || '—'}</Typography>
                      </Box>
                      {details.event_date && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Event Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(details.event_date)}</Typography>
                        </Box>
                      )}
                      {details.return_date && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Return Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(details.return_date)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Booking conversion</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {details.is_converted ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip icon={<CheckCircleIcon />} label="Converted" size="small" color="success" sx={{ fontWeight: 600 }} />
                              {details.booking_id && (
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => { setModalOpen(false); navigate('/bookings'); }}
                                  sx={{ fontWeight: 600 }}
                                >
                                  View Booking #{details.booking_id}
                                </Link>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Not converted</Typography>
                          )}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>Total Amount</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>₹{details.total_amount != null ? details.total_amount : 0}</Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0', height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#424242', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon color="primary" /> Products
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {(details.products || []).map((p, i) => (
                            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 1.25, border: '1px solid', borderColor: 'divider' }}>
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>{p.product_name}</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                                <span>Qty: {p.quantity}</span>
                                <span>₹{p.price}</span>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Product</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Qty</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Price</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(details.products || []).map((p, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>{p.product_name}</TableCell>
                                  <TableCell align="right">{p.quantity}</TableCell>
                                  <TableCell align="right">₹{p.price}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Card>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => openQuotationPdf(details.id)}
                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 3 }}
                  >
                    Export as PDF
                  </Button>
                </Box>
              </Box>
            ) : (
              <PageLoader variant="section" message="Loading quotation details..." />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setModalOpen(false)} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 3 }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Quotation Modal */}
        <QuotationEditModal
          isOpen={editModalOpen}
          quotation={editData}
          onClose={closeEdit}
          onSubmit={handleEditSubmit}
          onConvertSuccess={() => {
            closeEdit();
            fetchQuotations();
            showToast('Booking created successfully from quotation.', 'success');
          }}
        />
    </Box>
  );
};

export default Quotations;

const autocompleteSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiAutocomplete-inputRoot': {
    width: '100%',
  },
};

const editInputFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'divider' },
  },
  width: '100%',
};

const renderSearchInput = (placeholder) => (params) => (
  <TextField
    {...params}
    placeholder={placeholder}
    required
    size="small"
    fullWidth
    sx={editInputFieldSx}
  />
);

const renderSearchOption = (primary, secondary) => (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Box>
        <Typography sx={{ fontSize: '0.875rem' }}>{primary(option)}</Typography>
        {secondary(option) && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{secondary(option)}</Typography>
        )}
      </Box>
    </li>
  );
};

const filterProducts = (options, { inputValue }) => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;
  return options.filter((opt) => {
    const name = (opt.name || '').toLowerCase();
    const code = (opt.product_code || '').toLowerCase();
    const category = (opt.category || '').toLowerCase();
    return name.includes(search) || code.includes(search) || category.includes(search);
  });
};

const getCustomerLabel = (customer) => customer.company_name || customer.name || '';

const getProductLabel = (product) => product.name || '';

const filterCustomers = (options, { inputValue }) => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;
  return options.filter((c) => {
    const name = (c.name || '').toLowerCase();
    const company = (c.company_name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const place = (c.place || '').toLowerCase();
    const phone = (c.ph1 || '').toLowerCase();
    return (
      name.includes(search) ||
      company.includes(search) ||
      email.includes(search) ||
      place.includes(search) ||
      phone.includes(search)
    );
  });
};

// Edit modal for quotation
const QuotationEditModal = ({ isOpen, quotation, onClose, onSubmit, onConvertSuccess }) => {
  const [formData, setFormData] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [missingData, setMissingData] = useState(false);
  const showToast = useToast();
  const isConverted = quotation?.is_converted === true;

  useEffect(() => {
    let isMounted = true;
    const fetchAll = async () => {
      setLoadingData(true);
      try {
        const [prodRes, custRes] = await Promise.all([
          axios.get('/api/products', { params: { status: 'active' } }),
          axios.get('/api/customer'),
        ]);
        if (isMounted && quotation) {
          setProducts(prodRes.data);
          setCustomers(custRes.data);
          const customer_id = quotation.customer_id != null ? String(quotation.customer_id) : '';
          let productsArr = [{ product_id: '', quantity: '' }];
          let missing = false;
          if (quotation.products?.length) {
            productsArr = quotation.products.map((p) => ({
              product_id: p.product_id ? String(p.product_id) : '',
              quantity: p.quantity != null ? String(p.quantity) : ''
            }));
          } else {
            missing = true;
          }
          setFormData({
            customer_id: customer_id,
            event_date: quotation.event_date || '',
            return_date: quotation.return_date || '',
            products: productsArr
          });
          setMissingData(missing || !customer_id);
        }
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };
    if (isOpen && quotation) fetchAll();
    return () => { isMounted = false; };
  }, [isOpen, quotation]);

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index][field] = value;
    setFormData((prev) => ({ ...prev, products: newProducts }));
  };

  const addProductRow = () => {
    setFormData((prev) => ({ ...prev, products: [...prev.products, { product_id: '', quantity: '' }] }));
  };

  const removeProductRow = (index) => {
    if (formData.products.length === 1) return;
    setFormData((prev) => ({ ...prev, products: formData.products.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      const payload = {
        customer_id: parseInt(formData.customer_id),
        event_date: formData.event_date || null,
        return_date: formData.return_date || null,
        products: formData.products.map((p) => ({
          product_id: parseInt(p.product_id),
          quantity: parseInt(p.quantity)
        }))
      };
      await onSubmit(payload);
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed.';
      setErrorMsg(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async () => {
    if (!formData.event_date || !formData.event_date.trim()) {
      showToast('Please fill Event Date before creating Booking.', 'warning');
      return;
    }
    if (!formData.return_date || !formData.return_date.trim()) {
      showToast('Please fill Return Date before creating Booking.', 'warning');
      return;
    }
    setConverting(true);
    setErrorMsg('');
    try {
      const res = await axios.post(
        `/api/quotations/${quotation.id}/convert-to-booking`,
        { event_date: formData.event_date, return_date: formData.return_date }
      );
      if (onConvertSuccess) onConvertSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create booking.';
      setErrorMsg(msg);
      showToast(msg, 'error');
    } finally {
      setConverting(false);
    }
  };

  const modalPaperSx = {
    borderRadius: 2,
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    bgcolor: 'background.paper',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  };
  const dialogTitleSx = {
    fontSize: { xs: '1rem', sm: '1.125rem' },
    fontWeight: 600,
    color: 'text.primary',
    px: { xs: 2, sm: 3 },
    py: { xs: 1.5, sm: 2 },
    borderBottom: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
  const dialogPaperSx = { '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } };
  const labelSx = { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5, display: 'block' };
  const sectionTitleSx = { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1.5, display: 'block' };
  const inputFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.25, fontSize: '0.875rem', '& fieldset': { borderColor: 'divider' } } };

  if (!formData || loadingData) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: modalPaperSx }} sx={dialogPaperSx}>
        <DialogTitle sx={dialogTitleSx}>Edit Quotation</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
          <PageLoader variant="inline" message="Loading quotation..." />
        </DialogContent>
      </Dialog>
    );
  }

  if (isConverted) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: modalPaperSx }} sx={dialogPaperSx}>
        <DialogTitle sx={dialogTitleSx}>
          <span>Edit Quotation</span>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          <Alert severity="info">This quotation has been converted to a booking and cannot be edited.</Alert>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 500 }}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: modalPaperSx }} sx={dialogPaperSx}>
      <DialogTitle sx={dialogTitleSx}>
        <span>Edit Quotation</span>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
        {missingData && <Alert severity="warning" sx={{ mb: 2 }}>Some details could not be loaded. Please review before saving.</Alert>}
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        <Box component="form" onSubmit={handleSubmit} id="edit-quotation-form">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5, width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelSx}>Customer</Typography>
              <Autocomplete
                options={customers}
                value={customers.find((c) => String(c.id) === String(formData.customer_id)) || null}
                onChange={(_, newValue) =>
                  setFormData((prev) => ({ ...prev, customer_id: newValue ? String(newValue.id) : '' }))
                }
                getOptionLabel={getCustomerLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={filterCustomers}
                renderOption={renderSearchOption(
                  (option) => option.company_name || option.name,
                  (option) =>
                    option.company_name && option.name
                      ? option.name
                      : option.email || option.ph1 || option.place || ''
                )}
                renderInput={renderSearchInput('Search customer...')}
                fullWidth
                size="small"
                sx={autocompleteSx}
              />
            </Box>
            <Box sx={{ width: { xs: 140, sm: 152 }, flexShrink: 0 }} aria-hidden />
          </Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Event Date</Typography>
              <TextField
                type="date"
                name="event_date"
                value={formData.event_date}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Return Date</Typography>
              <TextField
                type="date"
                name="return_date"
                value={formData.return_date}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
          <Typography sx={sectionTitleSx}>Products</Typography>
          {formData.products.map((p, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                mb: 1.5,
                width: '100%',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={labelSx}>Product</Typography>
                <Autocomplete
                  options={products}
                  value={products.find((pr) => String(pr.id) === String(p.product_id)) || null}
                  onChange={(_, newValue) =>
                    handleProductChange(index, 'product_id', newValue ? String(newValue.id) : '')
                  }
                  getOptionLabel={getProductLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={filterProducts}
                  renderOption={renderSearchOption(
                    (option) => option.name,
                    (option) => {
                      const details = [
                        option.product_code,
                        option.category,
                        `₹${option.price ?? 0}`,
                      ].filter(Boolean);
                      return details.join(' · ');
                    }
                  )}
                  renderInput={renderSearchInput('Search product...')}
                  fullWidth
                  size="small"
                  sx={autocompleteSx}
                />
              </Box>
              <Box sx={{ width: { xs: 88, sm: 100 }, flexShrink: 0 }}>
                <Typography sx={labelSx}>Qty *</Typography>
                <TextField
                  type="number"
                  min="1"
                  value={p.quantity}
                  onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Qty"
                  sx={inputFieldSx}
                />
              </Box>
              <Box sx={{ flexShrink: 0, pt: 2.5 }}>
                <IconButton
                  size="small"
                  onClick={() => removeProductRow(index)}
                  disabled={formData.products.length === 1}
                  sx={{
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-disabled': { opacity: 0.5 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={addProductRow}
            sx={{ mb: 1, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Add Product
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={onClose} disabled={loading || converting} sx={{ textTransform: 'none', fontWeight: 500 }}>Cancel</Button>
        <Button variant="contained" color="success" onClick={handleCreateBooking} disabled={loading || converting} sx={{ textTransform: 'none', fontWeight: 500 }}>{converting ? 'Creating Booking...' : 'Create Booking'}</Button>
        <Button type="submit" form="edit-quotation-form" variant="contained" color="primary" disabled={loading || converting} sx={{ textTransform: 'none', fontWeight: 500 }}>{loading ? 'Updating...' : 'Update Quotation'}</Button>
      </DialogActions>
    </Dialog>
  );
};
